import { useMemo, useState, type ReactNode } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileSignature,
  FileText,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";

// ============================================================
// R3 — Jurídico (minutas + contratos por colaborador)
// ============================================================
// Arquivo preenchido pela frente jurídica sobre o esqueleto da R1 — mesmo
// nome de arquivo, mesmo nome de componente.
//
// O QUE ESTA TELA DIZ, E POR QUÊ:
//
//  1) A FAIXA DO TOPO NÃO SAI enquanto existir uma minuta sem aprovação do
//     advogado. Não é decoração: o texto que está aqui é ponto de partida
//     tirado de acervo e de modelo, e usar isso como contrato assinado é o
//     risco que já custou a ação da Jordana e do Mateus.
//
//  2) SÓCIO NÃO TEM CONTRATO. A palavra na tela é "Minuta societária (Holis)",
//     em todo lugar. Quem lê "contrato de sócio" entende vínculo de trabalho,
//     que é o oposto do que o documento faz.
//
//  3) O QUE FALTA APARECE EM VERMELHO E MESMO ASSIM GERA. O papel com os
//     buracos marcados é o que faz alguém completar o cadastro; recusar a
//     geração só empurra o problema para o dia da assinatura.
//
// Nenhum número, prazo ou regra é calculado aqui: tudo vem da clínica, que é
// a dona da ficha, do texto da minuta e da versão aprovada.

const AVISO_MINUTA =
  "Minutas são ponto de partida: nada é válido sem validação do advogado.";

const SETORES: Array<{ valor: "" | "clinica" | "agro" | "bt"; rotulo: string }> = [
  { valor: "", rotulo: "Todos os setores" },
  { valor: "clinica", rotulo: "Clínica" },
  { valor: "agro", rotulo: "AgroPet" },
  { valor: "bt", rotulo: "Banho e tosa" },
];

const MODALIDADES: Array<{
  valor: "pf_autonomo" | "pj" | "socio";
  rotulo: string;
  descricao: string;
}> = [
  {
    valor: "pf_autonomo",
    rotulo: "PF autônomo (restrita)",
    descricao: "Só para especialista sob demanda — não para quem cumpre escala fixa.",
  },
  { valor: "pj", rotulo: "PJ", descricao: "Prestador com CNPJ próprio." },
  {
    valor: "socio",
    rotulo: "Sócio minoritário",
    descricao: "Quotas na Holis. Gera minuta societária, nunca contrato.",
  },
];

const ESTILO_STATUS: Record<string, string> = {
  rascunho: "bg-slate-200 text-slate-700",
  advogado: "bg-amber-100 text-amber-800",
  assinado: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-slate-200 text-slate-500",
};

const ROTULO_STATUS: Record<string, string> = {
  rascunho: "Rascunho",
  advogado: "Em análise do advogado",
  assinado: "Assinado",
  cancelado: "Cancelado",
};

/** Placeholders que não vêm da ficha — o advogado calibra o valor deles. */
const PLACEHOLDERS_CALIBRAVEIS = [
  "objeto_servico",
  "valor_turno",
  "valor_hora",
  "dia_pagamento",
  "dias_aviso",
  "dias_aviso_socio",
  "meses_nao_concorrencia",
];

function rotuloModalidade(valor: string): string {
  return MODALIDADES.find((m) => m.valor === valor)?.rotulo ?? valor;
}

/** Sócio é minuta societária; o resto é contrato. A palavra importa. */
function palavraDoDocumento(modalidade: string, maiuscula = false): string {
  const p = modalidade === "socio" ? "minuta societária (Holis)" : "contrato";
  return maiuscula ? p.charAt(0).toUpperCase() + p.slice(1) : p;
}

function dataHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Abre o PDF que a clínica devolveu em base64 — mesmo caminho do recibo (F5):
 * tenta aba nova e, se o navegador bloquear, cai no download. O nome do
 * arquivo vem do servidor, que é a fonte única desse nome.
 */
function abrirPdfBase64(base64: string, nomeArquivo: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const aba = window.open(url, "_blank");
  if (!aba) {
    const a = document.createElement("a");
    a.href = url;
    a.download = nomeArquivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Os {{placeholders}} que o texto realmente usa, em ordem de aparição. */
function placeholdersDoCorpo(corpo: string): string[] {
  const achados = new Set<string>();
  const re = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(corpo)) !== null) achados.add(String(m[1]).toLowerCase());
  return Array.from(achados);
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <h2 className="text-sm font-semibold text-slate-900">{titulo}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Aba 1 — MINUTAS
// ============================================================
function AbaMinutas() {
  const { isAdmin } = usePermissions();
  const utils = trpc.useUtils();
  const [slugAberto, setSlugAberto] = useState<string | null>(null);
  const [corpo, setCorpo] = useState("");
  const [nota, setNota] = useState("");
  const [advogadoNome, setAdvogadoNome] = useState("");
  const [advogadoOab, setAdvogadoOab] = useState("");

  const q = trpc.juridico.templates.useQuery({ incluirCorpo: true }, { retry: false });
  const itens = q.data?.itens ?? [];
  const aberto = itens.find((t) => t.slug === slugAberto) ?? null;

  function abrir(slug: string) {
    const t = itens.find((x) => x.slug === slug);
    setSlugAberto(slug);
    setCorpo(String(t?.corpoHtml ?? ""));
    setNota("");
    setAdvogadoNome("");
    setAdvogadoOab("");
  }

  const salvar = trpc.juridico.salvarMinuta.useMutation({
    onSuccess: (r) => {
      utils.juridico.templates.invalidate();
      alert(
        r.versaoNova
          ? `Texto salvo como versão ${r.template.versao}. A aprovação anterior caiu: aprovação é de um texto, e o texto mudou — o advogado precisa aprovar de novo.`
          : "Nada mudou no texto; a versão continua a mesma.",
      );
    },
    onError: (e) => alert(e.message),
  });

  const aprovar = trpc.juridico.aprovarMinuta.useMutation({
    onSuccess: (r) => {
      utils.juridico.templates.invalidate();
      setAdvogadoNome("");
      setAdvogadoOab("");
      alert(`Versão ${r.template.versao} marcada como aprovada pelo advogado.`);
    },
    onError: (e) => alert(e.message),
  });

  const reprovar = trpc.juridico.reprovarMinuta.useMutation({
    onSuccess: () => {
      utils.juridico.templates.invalidate();
      alert("Aprovação retirada. Os documentos gerados a partir de agora voltam a sair como minuta.");
    },
    onError: (e) => alert(e.message),
  });

  const ocupado = salvar.isPending || aprovar.isPending || reprovar.isPending;

  if (q.isLoading) {
    return <p className="text-sm text-slate-500">Carregando os modelos...</p>;
  }
  if (q.error) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-sm text-red-700">{q.error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Secao titulo="Modelos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-2 py-2 font-medium">Modelo</th>
                <th className="px-2 py-2 font-medium">Modalidade</th>
                <th className="px-2 py-2 font-medium">Versão</th>
                <th className="px-2 py-2 font-medium">Advogado</th>
                <th className="px-2 py-2 font-medium">Ativo</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {itens.map((t) => (
                <tr key={t.slug} className="border-b border-slate-100 align-top">
                  <td className="px-2 py-3">
                    <div className="font-medium text-slate-900">{t.titulo}</div>
                    <div className="font-mono text-xs text-slate-400">{t.slug}</div>
                  </td>
                  <td className="px-2 py-3 text-slate-600">{rotuloModalidade(t.modalidade)}</td>
                  <td className="px-2 py-3 text-slate-600">v{t.versao}</td>
                  <td className="px-2 py-3">
                    {t.aprovadoPorAdvogado ? (
                      <div>
                        <Badge className="bg-emerald-100 text-emerald-800">Aprovada</Badge>
                        {/* Quem assinou, por extenso. Sem isto a coluna dizia
                            só "Aprovada" e a data — e aprovação sem autor é
                            exatamente o buraco que este módulo fecha. */}
                        <div className="mt-1 text-xs font-medium text-slate-700">
                          {t.advogadoNome ?? "advogado não identificado"}
                          {t.advogadoOab ? ` · OAB ${t.advogadoOab}` : ""}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-500">
                          {t.aprovadoEm ? dataHora(t.aprovadoEm) : "—"}
                        </div>
                      </div>
                    ) : (
                      <Badge className="bg-red-100 text-red-800">Não aprovada</Badge>
                    )}
                  </td>
                  <td className="px-2 py-3 text-slate-600">{t.ativo ? "Sim" : "Não"}</td>
                  <td className="px-2 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (slugAberto === t.slug ? setSlugAberto(null) : abrir(t.slug))}
                    >
                      {slugAberto === t.slug ? "Fechar" : "Abrir"}
                    </Button>
                  </td>
                </tr>
              ))}
              {itens.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-2 py-6 text-center text-sm text-slate-500">
                    Nenhum modelo cadastrado na clínica ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Secao>

      {aberto && (
        <Secao titulo={`${aberto.titulo} — versão ${aberto.versao}`}>
          {aberto.versaoNota && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-600">Nota da versão</p>
              <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{aberto.versaoNota}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-slate-600">Placeholders usados por este texto</p>
            <p className="mt-1 text-xs text-slate-500">
              Os da ficha são preenchidos pela clínica na hora de gerar. Se algum ficar vazio, o
              documento sai com <span className="font-mono">[ __________ ]</span> e o campo entra na
              lista vermelha.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {placeholdersDoCorpo(corpo).map((p) => {
                const calibravel = PLACEHOLDERS_CALIBRAVEIS.includes(p);
                return (
                  <span
                    key={p}
                    title={
                      calibravel
                        ? "Valor calibrado pelo advogado (padrão do sistema)"
                        : "Vem da ficha do colaborador na clínica"
                    }
                    className={
                      "rounded px-1.5 py-0.5 font-mono text-xs ring-1 " +
                      (calibravel
                        ? "bg-amber-50 text-amber-800 ring-amber-200"
                        : "bg-slate-100 text-slate-700 ring-slate-200")
                    }
                  >
                    {"{{" + p + "}}"}
                  </span>
                );
              })}
              {placeholdersDoCorpo(corpo).length === 0 && (
                <span className="text-xs text-slate-400">Nenhum placeholder neste texto.</span>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600">Corpo da minuta (HTML)</label>
            <textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              readOnly={!isAdmin}
              spellCheck={false}
              rows={18}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white p-3 font-mono text-xs text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400 disabled:bg-slate-50"
            />
            <p className="mt-1 text-xs text-slate-500">
              Salvar um texto diferente sobe a versão e derruba a aprovação anterior. É de propósito:
              o advogado aprovou um texto, não um nome de modelo.
            </p>
          </div>

          {isAdmin ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Nota desta versão (o que mudou, o que o advogado precisa olhar)
                </label>
                <Input
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  placeholder="Ex.: ajustei a cláusula de quarentena para 18 meses."
                  className="mt-1"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={ocupado || corpo.trim().length < 200}
                  onClick={() =>
                    salvar.mutate({
                      slug: aberto.slug,
                      corpoHtml: corpo,
                      versaoNota: nota.trim() ? nota.trim() : null,
                    })
                  }
                >
                  {salvar.isPending ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="mr-1.5 h-4 w-4" />
                  )}
                  Salvar texto (sobe a versão)
                </Button>
              </div>

              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-xs font-medium text-slate-700">Aprovação do advogado</p>
                <p className="mt-1 text-xs text-slate-500">
                  Só marque depois que um advogado leu ESTA versão. O nome, a OAB e a data ficam
                  gravados na nota da versão, junto com quem registrou.
                </p>
                {aberto.aprovadoPorAdvogado ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Badge className="bg-emerald-100 text-emerald-800">
                      Aprovada em {dataHora(aberto.aprovadoEm)}
                      {aberto.advogadoNome ? ` por ${aberto.advogadoNome}` : ""}
                      {aberto.advogadoOab ? ` (OAB ${aberto.advogadoOab})` : ""}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={ocupado}
                      onClick={() => {
                        const motivo = window.prompt(
                          "Por que a aprovação está sendo retirada? (fica registrado na nota da versão)",
                        );
                        if (!motivo || motivo.trim().length < 5) return;
                        reprovar.mutate({
                          slug: aberto.slug,
                          motivo: motivo.trim(),
                          notaAtual: aberto.versaoNota ?? null,
                        });
                      }}
                    >
                      Retirar aprovação
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3 grid gap-2 sm:grid-cols-[1fr,160px,auto]">
                    <Input
                      value={advogadoNome}
                      onChange={(e) => setAdvogadoNome(e.target.value)}
                      placeholder="Nome do advogado"
                    />
                    <Input
                      value={advogadoOab}
                      onChange={(e) => setAdvogadoOab(e.target.value)}
                      placeholder="OAB (opcional)"
                    />
                    <Button
                      size="sm"
                      disabled={ocupado || advogadoNome.trim().length < 3}
                      onClick={() =>
                        aprovar.mutate({
                          slug: aberto.slug,
                          advogadoNome: advogadoNome.trim(),
                          advogadoOab: advogadoOab.trim() ? advogadoOab.trim() : null,
                          notaAtual: aberto.versaoNota ?? null,
                        })
                      }
                    >
                      {aprovar.isPending ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-1.5 h-4 w-4" />
                      )}
                      Marcar como aprovada pelo advogado
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              Só o administrador altera o texto da minuta e registra a aprovação do advogado.
            </p>
          )}
        </Secao>
      )}
    </div>
  );
}

// ============================================================
// Aba 2 — CONTRATOS POR COLABORADOR
// ============================================================
function AbaContratos() {
  // Gerar e mudar andamento são do financeiro/admin (a clínica revalida). O
  // papel 'juridico' lê, baixa o PDF e acompanha o status.
  const { isFinanceiro } = usePermissions();
  const utils = trpc.useUtils();
  const [setor, setSetor] = useState<"" | "clinica" | "agro" | "bt">("");
  const [busca, setBusca] = useState("");
  const [profissionalId, setProfissionalId] = useState<number | null>(null);
  const [modalidade, setModalidade] = useState<"pf_autonomo" | "pj" | "socio">("pj");
  const [observacoes, setObservacoes] = useState("");

  const pessoas = trpc.juridico.colaboradores.useQuery(
    { setor: setor === "" ? null : setor, incluirInativos: false },
    { retry: false },
  );

  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const todas = pessoas.data ?? [];
    return termo ? todas.filter((p) => p.nome.toLowerCase().includes(termo)) : todas;
  }, [pessoas.data, busca]);

  const selecionada = (pessoas.data ?? []).find((p) => p.id === profissionalId) ?? null;

  const ficha = trpc.juridico.ficha.useQuery(
    { profissionalId: profissionalId ?? 0, modalidade },
    { enabled: profissionalId !== null && Boolean(selecionada?.vinculado), retry: false },
  );

  const contratos = trpc.juridico.contratos.useQuery(
    { profissionalId: profissionalId ?? 0 },
    { enabled: profissionalId !== null && Boolean(selecionada?.vinculado), retry: false },
  );

  const gerar = trpc.juridico.gerar.useMutation({
    onSuccess: (r) => {
      utils.juridico.contratos.invalidate();
      utils.juridico.ficha.invalidate();
      setObservacoes("");
      const doc = palavraDoDocumento(modalidade, true);
      if (r.faltantes.length > 0) {
        alert(
          `${doc} gerado em RASCUNHO, com ${r.faltantes.length} pendência(s) de cadastro. ` +
            "O PDF sai com a lista em vermelho e não vale como documento até a ficha ser completada.",
        );
      } else if (!r.modeloAprovadoPorAdvogado) {
        alert(
          `${doc} gerado, mas o modelo ainda é MINUTA: nenhuma versão foi aprovada pelo advogado. ` +
            "O PDF sai com marca d'água e não pode ser registrado como assinado.",
        );
      } else {
        alert(`${doc} gerado em rascunho, sem pendência de cadastro.`);
      }
    },
    onError: (e) => alert(e.message),
  });

  const pdf = trpc.juridico.pdf.useMutation({
    onSuccess: (r) => abrirPdfBase64(r.base64, r.nomeArquivo),
    onError: (e) => alert(e.message),
  });

  const status = trpc.juridico.mudarStatus.useMutation({
    onSuccess: (r) => {
      utils.juridico.contratos.invalidate();
      utils.juridico.ficha.invalidate();
      alert(`Andamento atualizado: ${ROTULO_STATUS[r.de] ?? r.de} → ${ROTULO_STATUS[r.para] ?? r.para}.`);
    },
    onError: (e) => alert(e.message),
  });

  const ocupado = gerar.isPending || pdf.isPending || status.isPending;
  const checklist = ficha.data?.checklist ?? null;
  const itensContrato = contratos.data?.itens ?? [];

  function mudarStatus(contratoId: number, novo: "advogado" | "assinado" | "cancelado" | "rascunho") {
    const observacao = window.prompt(
      `Observação para o andamento "${ROTULO_STATUS[novo]}" (opcional, fica no histórico):`,
      "",
    );
    // prompt cancelado = desistiu; string vazia = seguiu sem observação.
    if (observacao === null) return;
    status.mutate({
      contratoId,
      status: novo,
      observacao: observacao.trim() ? observacao.trim() : null,
    });
  }

  return (
    <div className="space-y-6">
      <Secao titulo="Colaborador">
        <div className="grid gap-3 sm:grid-cols-[200px,1fr]">
          <div>
            <label className="text-xs font-medium text-slate-600">Setor</label>
            <select
              value={setor}
              onChange={(e) => {
                setSetor(e.target.value as any);
                setProfissionalId(null);
              }}
              className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
            >
              {SETORES.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.rotulo}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Buscar pelo nome</label>
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Nome do colaborador"
              className="mt-1"
            />
          </div>
        </div>

        {pessoas.isLoading ? (
          <p className="text-sm text-slate-500">Carregando a equipe...</p>
        ) : (
          <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
            {lista.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setProfissionalId(p.id)}
                className={
                  "flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50 " +
                  (p.id === profissionalId ? "bg-slate-100 font-medium text-slate-900" : "text-slate-700")
                }
              >
                <span>{p.nome}</span>
                <span className="flex items-center gap-2">
                  {p.setor && (
                    <span className="text-xs text-slate-400">
                      {SETORES.find((s) => s.valor === p.setor)?.rotulo ?? p.setor}
                    </span>
                  )}
                  {!p.vinculado && (
                    <Badge className="bg-amber-100 text-amber-800">Sem ficha na clínica</Badge>
                  )}
                </span>
              </button>
            ))}
            {lista.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-slate-500">
                Ninguém neste filtro.
              </p>
            )}
          </div>
        )}
      </Secao>

      {selecionada && !selecionada.vinculado && (
        <Card>
          <CardContent className="py-5">
            <p className="text-sm text-slate-700">
              {selecionada.nome} ainda não está vinculado a uma ficha da clínica. Sem CPF/CNPJ e
              endereço não existe documento — resolva em Configurações &gt; Vínculos primeiro.
            </p>
          </CardContent>
        </Card>
      )}

      {selecionada && selecionada.vinculado && (
        <>
          <Secao titulo={`Gerar documento — ${selecionada.nome}`}>
            <div className="grid gap-3 sm:grid-cols-[240px,1fr]">
              <div>
                <label className="text-xs font-medium text-slate-600">Modalidade</label>
                <select
                  value={modalidade}
                  onChange={(e) => setModalidade(e.target.value as any)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
                >
                  {MODALIDADES.map((m) => (
                    <option key={m.valor} value={m.valor}>
                      {m.rotulo}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  {MODALIDADES.find((m) => m.valor === modalidade)?.descricao}
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Observação (opcional)</label>
                <Input
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  placeholder="Ex.: substitui o documento anterior, revisado em reunião."
                  className="mt-1"
                />
              </div>
            </div>

            <p className="text-xs text-slate-500">
              CLT não sai por aqui: registro em carteira é ato do contador, não documento gerado
              pelo sistema.
            </p>

            {ficha.isLoading && <p className="text-sm text-slate-500">Consultando a ficha na clínica...</p>}
            {ficha.error && <p className="text-sm text-red-700">{ficha.error.message}</p>}

            {checklist && checklist.totalPendencias > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{checklist.resumo}</p>
                    <ul className="mt-2 space-y-1">
                      {[...checklist.bloqueios, ...checklist.pendencias].map((item) => (
                        <li key={`${item.grupo}-${item.campo}`} className="text-xs text-red-700">
                          <span className="font-medium">{item.rotulo}</span>
                          {item.severidade === "bloqueio" && (
                            <span className="ml-1 font-semibold uppercase">· impedimento</span>
                          )}
                          <span className="text-red-600"> — {item.motivo}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-red-700">
                      Estes campos moram na ficha da clínica. Gerar assim produz rascunho com os
                      buracos marcados, e o documento não pode ser registrado como assinado.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {checklist && checklist.totalPendencias === 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm text-emerald-800">{checklist.resumo}</p>
              </div>
            )}

            {checklist && checklist.alertas.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-medium text-amber-800">Avisos que não travam</p>
                <ul className="mt-1 space-y-1">
                  {checklist.alertas.map((item) => (
                    <li key={`a-${item.grupo}-${item.campo}`} className="text-xs text-amber-800">
                      <span className="font-medium">{item.rotulo}</span> — {item.motivo}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isFinanceiro && (<div>
              <Button
                size="sm"
                disabled={ocupado || profissionalId === null}
                onClick={() =>
                  gerar.mutate({
                    profissionalId: profissionalId as number,
                    modalidade,
                    observacoes: observacoes.trim() ? observacoes.trim() : null,
                  })
                }
              >
                {gerar.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <FileSignature className="mr-1.5 h-4 w-4" />
                )}
                Gerar {palavraDoDocumento(modalidade)}
              </Button>
            </div>)}
          </Secao>

          <Secao titulo="Histórico de documentos">
            {contratos.isLoading && <p className="text-sm text-slate-500">Carregando...</p>}
            {contratos.error && <p className="text-sm text-red-700">{contratos.error.message}</p>}

            {!contratos.isLoading && itensContrato.length === 0 && (
              <p className="text-sm text-slate-500">
                Nenhum documento gerado para {selecionada.nome} ainda.
              </p>
            )}

            <div className="space-y-3">
              {itensContrato.map((c) => (
                <div key={c.id} className="rounded-md border border-slate-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {palavraDoDocumento(c.modalidade, true)} · {rotuloModalidade(c.modalidade)}
                      </p>
                      <p className="text-xs text-slate-500">
                        <span className="font-mono">{c.templateSlug}</span> v{c.templateVersao} ·
                        gerado em {dataHora(c.geradoEm)}
                        {c.geradoPor ? ` por ${c.geradoPor}` : ""}
                      </p>
                      {c.observacoes && (
                        <p className="mt-1 text-xs text-slate-600">{c.observacoes}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={ESTILO_STATUS[c.status] ?? "bg-slate-200 text-slate-700"}>
                        {c.statusRotulo ?? ROTULO_STATUS[c.status] ?? c.status}
                      </Badge>
                      {c.modeloAprovado === false && (
                        <Badge className="bg-red-100 text-red-800">Modelo não aprovado</Badge>
                      )}
                    </div>
                  </div>

                  {c.faltantes.length > 0 && (
                    <div className="mt-2 rounded border border-red-200 bg-red-50 p-2">
                      <p className="text-xs font-medium text-red-800">
                        {c.faltantes.length} pendência(s) de cadastro congelada(s) neste papel
                      </p>
                      <ul className="mt-1 list-disc pl-4">
                        {c.faltantes.map((f, i) => (
                          <li key={`${c.id}-f-${i}`} className="text-xs text-red-700">
                            {f}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1 text-xs text-red-700">
                        Completar a ficha não conserta este documento: gere outro depois.
                      </p>
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={ocupado}
                      onClick={() => pdf.mutate({ contratoId: c.id })}
                    >
                      {pdf.isPending ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="mr-1.5 h-4 w-4" />
                      )}
                      Baixar PDF
                    </Button>
                    {isFinanceiro && c.status === "rascunho" && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={ocupado}
                        onClick={() => mudarStatus(c.id, "advogado")}
                      >
                        Enviar ao advogado
                      </Button>
                    )}
                    {isFinanceiro && (c.status === "rascunho" || c.status === "advogado") && (
                      <>
                        <Button
                          size="sm"
                          disabled={ocupado}
                          onClick={() => mudarStatus(c.id, "assinado")}
                        >
                          Registrar assinatura
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={ocupado}
                          onClick={() => mudarStatus(c.id, "cancelado")}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                    {isFinanceiro && c.status === "advogado" && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={ocupado}
                        onClick={() => mudarStatus(c.id, "rascunho")}
                      >
                        Voltar para rascunho
                      </Button>
                    )}
                  </div>

                  {c.status === "assinado" && (
                    <p className="mt-2 text-xs text-slate-500">
                      Assinatura registrada em {dataHora(c.assinadoEm)}.
                    </p>
                  )}
                  {c.status === "cancelado" && (
                    <p className="mt-2 text-xs text-slate-500">
                      Cancelado em {dataHora(c.canceladoEm)}. Documento cancelado não volta atrás —
                      gere outro.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Secao>
        </>
      )}
    </div>
  );
}

export function Juridico() {
  const q = trpc.juridico.templates.useQuery({ incluirCorpo: true }, { retry: false });
  const naoAprovadas = q.data?.semAprovacao ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Jurídico</h1>
          <p className="mt-1 text-sm text-slate-500">
            Contratos por modalidade, modelos aprovados pelo advogado e o andamento de cada
            assinatura.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          <RefreshCw className={"mr-1.5 h-4 w-4 " + (q.isFetching ? "animate-spin" : "")} />
          Atualizar
        </Button>
      </div>

      {naoAprovadas > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
          <div>
            <p className="text-sm font-medium text-red-800">{AVISO_MINUTA}</p>
            <p className="mt-0.5 text-xs text-red-700">
              {naoAprovadas === 1
                ? "1 modelo ainda está sem aprovação."
                : `${naoAprovadas} modelos ainda estão sem aprovação.`}{" "}
              Os documentos gerados a partir deles saem com marca d'água e não podem ser registrados
              como assinados.
            </p>
          </div>
        </div>
      )}

      <Tabs defaultValue="contratos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="contratos">Contratos por colaborador</TabsTrigger>
          <TabsTrigger value="minutas">Minutas</TabsTrigger>
        </TabsList>
        <TabsContent value="contratos">
          <AbaContratos />
        </TabsContent>
        <TabsContent value="minutas">
          <AbaMinutas />
        </TabsContent>
      </Tabs>
    </div>
  );
}
